import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { VIDEOS, IMAGES } from "../lib/media";

const safePlay = (v) => v?.play?.()?.catch?.(() => {});

function VideoCard({ video, index }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) { ref.current.muted = true; ref.current.defaultMuted = true; } }, []);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { safePlay(v); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={`relative rounded-xl overflow-hidden border border-ib-steel/20 bg-ib-surface group ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
      data-testid={`video-card-${video.id}`}
    >
      <video
        ref={ref}
        src={video.src}
        poster={video.poster}
        muted
        loop
        playsInline
        preload="metadata"
        onPointerEnter={(e) => { if (e.pointerType !== "mouse") return; safePlay(e.currentTarget); setPlaying(true); }}
        onPointerLeave={(e) => { if (e.pointerType !== "mouse") return; e.currentTarget.pause(); setPlaying(false); }}
        onClick={toggle}
        className={`w-full object-cover ${index === 0 ? "h-full min-h-[320px]" : "h-48 md:h-full min-h-[160px]"}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ib-bg/90 via-ib-bg/10 to-transparent pointer-events-none" />
      <button onClick={toggle} data-testid={`video-toggle-${video.id}`} aria-label={playing ? "Pause" : "Play"}
        className="absolute top-3 right-3 p-2 rounded-full glass text-ib-text hover:text-ib-lime transition-colors">
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="font-outfit font-bold text-sm text-ib-text">{video.title}</div>
        <div className="font-manrope text-[11px] text-ib-text-secondary">{video.caption}</div>
      </div>
    </motion.div>
  );
}

export function SteelShowcase() {
  return (
    <section id="showcase" data-testid="showcase-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">Inside the industry</span>
            <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
              Steel is heavy. <span className="text-ib-blue">Trading it shouldn't be.</span>
            </h2>
            <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4">
              From molten metal to TMT bundles on your site — see the world IronByte digitises. Hover or tap any clip to play.
            </p>
          </div>
          <img src={IMAGES.tmtYard} alt="TMT bars stacked in a yard" loading="lazy"
            className="hidden md:block w-56 h-36 object-cover rounded-xl border border-ib-steel/20 rotate-2 hover:rotate-0 transition-transform duration-500" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-4" data-testid="video-grid">
          {VIDEOS.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
        </div>
      </div>
    </section>
  );
}
